import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CreditCard, 
  Calendar,
  Download,
  ExternalLink,
  CheckCircle2,
  Clock,
  ArrowRight,
  Shield
} from 'lucide-react';
import { format } from 'date-fns';
import { useBillingPage } from '../hooks/useBillingPage';
import { useCheckoutSession } from '../hooks/useCheckoutSession';
import { usePortalSession } from '../hooks/usePortalSession';
import { PricingCard } from '../components/billing/PricingCard';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatLocaleDate, formatLocaleCurrency } from '../utils/dateUtils';
import { PageLayout } from '../components/layout';

// Define the BillingData interface based on usage

interface PlanOption {
  priceId: string;
  productName: string;
  description: string;
  formattedPrice: string;
  formattedMonthlyPrice?: string;
  buttonText: string;
  features: string[];
}

interface InvoiceDetails {
  id: string;
  number: string;
  amount_paid: number;
  currency: string;
  created: number;
  status: string;
  invoice_pdf: string;
  hosted_invoice_url: string;
}

// Import interface from the hook - removed local duplicate definitions

const Billing = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get billing period from URL or default to monthly
  const periodParam = searchParams.get('period');
  const initialPeriod = (periodParam === 'yearly' || periodParam === 'monthly') 
    ? periodParam 
    : 'monthly';
  
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(initialPeriod as 'monthly' | 'yearly');
  // Use the hook without generic type
  const { data: billingData, isLoading, error } = useBillingPage();
  const checkoutMutation = useCheckoutSession();
  const portalMutation = usePortalSession();
  // Re-introduce derived hasSubscription, guarded by the billingData check later
  const hasSubscription = billingData?.hasSubscription;

  // Set document title
  useDocumentTitle();

  // Update URL when billing period changes
  useEffect(() => {
    navigate(`/billing?period=${billingPeriod}`, { replace: true });
  }, [billingPeriod, navigate]);

  const handlePeriodChange = (period: 'monthly' | 'yearly') => {
    setBillingPeriod(period);
  };

  const getButtonType = (plan: any): 'subscribe' | 'upgrade' | 'downgrade' | 'saveWithYearly' | 'current' => {
    if (!hasSubscription || !billingData.currentSubscription?.currentPlan) {
      return 'subscribe';
    }

    const currentPlan = billingData.currentSubscription.currentPlan;
    
    // If it's the exact same plan (same priceId)
    if (currentPlan.priceId === plan.priceId) {
      return 'current';
    }

    // Check if it's the same product but different billing period
    const currentPlanName = currentPlan.productName || '';
    const planName = plan.productName || '';
    
    // If same plan name but currently monthly and viewing yearly
    if (currentPlanName === planName && 
        currentPlan.interval === 'month' && 
        billingPeriod === 'yearly') {
      return 'saveWithYearly';
    }

    // Define plan hierarchy based on your actual plan names
    const planHierarchy = ['Solo', 'Team', 'Agency Pro'];
    
    const currentPlanIndex = planHierarchy.findIndex(p => 
      currentPlanName.toLowerCase().includes(p.toLowerCase())
    );
    const targetPlanIndex = planHierarchy.findIndex(p => 
      planName.toLowerCase().includes(p.toLowerCase())
    );

    // If we can determine hierarchy from names
    if (currentPlanIndex !== -1 && targetPlanIndex !== -1) {
      if (targetPlanIndex > currentPlanIndex) {
        return 'upgrade';
      } else if (targetPlanIndex < currentPlanIndex) {
        return 'downgrade';
      }
    }

    // Fallback: try to parse amounts from formatted prices
    const parseAmount = (formattedPrice: string): number => {
      // Remove currency symbols and parse the number
      const cleanPrice = formattedPrice.replace(/[€$£,\s]/g, '');
      const match = cleanPrice.match(/\d+\.?\d*/);
      return match ? parseFloat(match[0]) : 0;
    };

    const currentAmount = currentPlan.amount || parseAmount(currentPlan.formattedPrice || '0');
    const planAmount = plan.amount || parseAmount(plan.formattedPrice || '0');

    if (planAmount > currentAmount) {
      return 'upgrade';
    } else if (planAmount < currentAmount) {
      return 'downgrade';
    }

    return 'subscribe';
  };

  const getCardIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return (
          <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded shadow-sm text-white text-xs font-bold flex items-center justify-center">
            VISA
          </div>
        );
      case 'mastercard':
        return (
          <div className="w-12 h-8 bg-gradient-to-r from-red-600 to-red-700 rounded shadow-sm text-white text-xs font-bold flex items-center justify-center relative overflow-hidden">
            <div className="absolute left-1 w-3 h-3 bg-red-400 rounded-full opacity-80"></div>
            <div className="absolute right-1 w-3 h-3 bg-yellow-400 rounded-full opacity-80"></div>
            <span className="relative z-10">MC</span>
          </div>
        );
      case 'amex':
        return (
          <div className="w-12 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded shadow-sm text-white text-xs font-bold flex items-center justify-center">
            AMEX
          </div>
        );
      case 'discover':
        return (
          <div className="w-12 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded shadow-sm text-white text-xs font-bold flex items-center justify-center">
            DISC
          </div>
        );
      default:
        return (
          <div className="w-12 h-8 bg-gradient-to-r from-gray-500 to-gray-600 rounded shadow-sm text-white text-xs font-bold flex items-center justify-center">
            <CreditCard className="w-4 h-4" />
          </div>
        );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-700';
      case 'canceled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0">
      <div className="w-[95%] mx-auto">
        {isLoading ? (
          // Skeleton Loader for Billing Page
          <div className="py-12 animate-pulse">
            {/* Header Skeleton */}
            <div className="mb-8">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
            {/* Current Plan/Payment Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2 bg-gray-100 rounded-xl border border-gray-200 p-6 h-48"></div>
              <div className="bg-gray-100 rounded-xl border border-gray-200 p-6 h-48"></div>
            </div>
            {/* Toggle Skeleton */}
            <div className="flex justify-center mb-8">
              <div className="h-10 w-64 bg-gray-200 rounded-lg"></div>
            </div>
            {/* Pricing Cards Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-gray-100 rounded-xl border border-gray-200 p-6 h-96"></div>
              ))}
            </div>
            {/* Billing History Skeleton */}
            <div className="bg-gray-100 rounded-xl border border-gray-200 p-6 h-64"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center min-h-screen">
            <p className="text-red-600">{t('billing.loadingError')}</p>
          </div>
        ) : !billingData ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <PageLayout
            title={t('billing.title')}
            subtitle={t('billing.subtitle')}
          >

            {!hasSubscription ? (
              <div className="bg-white rounded-xl border border-black shadow-lg p-6 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-gray-900">{t('billing.noActiveSubscription')}</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  {t('billing.noActiveSubscriptionMessage')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Current Plan Card */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-black shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-primary-600" />
                      <h2 className="text-lg font-semibold text-gray-900">{t('billing.currentPlan')}</h2>
                    </div>
                    {billingData.currentSubscription?.status && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(billingData.currentSubscription.status)}`}>
                        {billingData.currentSubscription.status === 'active' ? (
                          <>
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {t('billing.active')}
                          </>
                        ) : (
                          <>
                            <Clock className="w-3 h-3 mr-1" />
                            {billingData.currentSubscription.status}
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="space-y-6">
                    {billingData.currentSubscription?.currentPlan && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-gray-900">
                            {formatLocaleCurrency(billingData.currentSubscription.currentPlan.amount, billingData.currentSubscription.currentPlan.currency, i18n.language)}
                          </span>
                          <span className="text-gray-500">/ {t(`billing.intervals.${billingData.currentSubscription.currentPlan.interval}`)}</span>
                        </div>
                        <button
                          onClick={() => portalMutation.mutate(window.location.origin + '/billing')}
                          disabled={portalMutation.isPending}
                          className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CreditCard className="w-3 h-3 mr-1.5" />
                          {portalMutation.isPending ? t('common.loading') : t('billing.manageSubscription')}
                        </button>
                      </div>
                    )}
                    {billingData.currentSubscription?.endDate && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{t('billing.currentPeriodEnds')} {format(new Date(billingData.currentSubscription.endDate), 'MMM d, yyyy')}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Payment Method Card */}
                <div className="bg-white rounded-xl border border-black shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-gray-400" />
                      <h2 className="text-lg font-semibold text-gray-900">{t('billing.paymentMethod')}</h2>
                    </div>
                    {billingData.currentSubscription?.paymentMethod && (
                      <button
                        onClick={() => portalMutation.mutate(window.location.origin + '/billing')}
                        disabled={portalMutation.isPending}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors disabled:opacity-50"
                      >
                        {portalMutation.isPending ? t('common.loading') : t('billing.edit')}
                      </button>
                    )}
                  </div>
                  
                  {billingData.currentSubscription?.paymentMethod ? (
                    <div className="relative w-full h-36 rounded-2xl overflow-hidden shadow-xl mb-4">
                      {/* Credit Card Background - Blue gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800">
                        {/* Subtle background pattern */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-blue-900/10"></div>
                      </div>
                      
                      {/* Card Content */}
                      <div className="relative h-full p-4 flex flex-col justify-between text-white">
                        {/* Middle - Card Number */}
                        <div className="flex-1 flex items-center">
                          <div className="text-xl font-mono tracking-wider font-medium">
                            •••• •••• •••• {billingData.currentSubscription.paymentMethod.last4}
                          </div>
                        </div>

                        {/* Bottom Row - Expiry and Logo */}
                        <div className="flex items-end justify-between">
                          <div className="text-left">
                            <div className="text-xs opacity-75 mb-1">{t('billing.expires')}</div>
                            <div className="text-sm font-mono">
                              {String(billingData.currentSubscription.paymentMethod.expMonth).padStart(2, '0')}/{String(billingData.currentSubscription.paymentMethod.expYear).slice(-2)}
                            </div>
                          </div>
                          
                          {/* Brand Logo - Bottom Right */}
                          <div className="flex-shrink-0">
                            {billingData.currentSubscription.paymentMethod.brand?.toLowerCase() === 'visa' ? (
                              <img 
                                src="/assets/visa-2021.svg" 
                                alt="Visa"
                                className="h-6 w-auto object-contain filter brightness-0 invert"
                              />
                            ) : billingData.currentSubscription.paymentMethod.brand?.toLowerCase() === 'mastercard' ? (
                              <img 
                                src="/assets/mastercard-2019.svg" 
                                alt="MasterCard"
                                className="h-6 w-auto object-contain"
                              />
                            ) : billingData.currentSubscription.paymentMethod.brand?.toLowerCase() === 'amex' || 
                              billingData.currentSubscription.paymentMethod.brand?.toLowerCase() === 'american express' ? (
                              <div className="text-white text-sm font-bold bg-white/10 px-2 py-1 rounded">
                                AMEX
                              </div>
                            ) : billingData.currentSubscription.paymentMethod.brand ? (
                              <div className="bg-white/20 backdrop-blur-sm rounded px-2 py-1 text-xs font-bold text-white">
                                {billingData.currentSubscription.paymentMethod.brand.toUpperCase()}
                              </div>
                            ) : (
                              <div className="bg-white/20 backdrop-blur-sm rounded px-2 py-1 text-xs font-bold text-white">
                                CARD
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg mb-4 text-gray-500">
                      <CreditCard className="w-6 h-6" />
                      <div>
                        <p className="text-sm">{t('billing.noPaymentMethod')}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Billing Period Toggle */}
            <div className="flex justify-center items-center gap-8 mb-8">
              <div className="flex items-center gap-8 bg-primary-600 p-1 rounded-lg">
                <button
                  onClick={() => handlePeriodChange('monthly')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingPeriod === 'monthly'
                      ? 'bg-white text-primary-600'
                      : 'bg-primary-600 text-white'
                  }`}
                >
                  {t('billing.monthly')}
                </button>
                <button
                  onClick={() => handlePeriodChange('yearly')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingPeriod === 'yearly'
                      ? 'bg-white text-primary-600'
                      : 'bg-primary-600 text-white'
                  }`}
                >
                  {t('billing.yearly')}
                  <span className="ml-2 inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                    {t('billing.save20Percent')}
                  </span>
                </button>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {billingData?.planOptions[billingPeriod].map((plan) => {
                // Check if this is the current plan by comparing priceId
                const isCurrentPlan = billingData.currentSubscription?.currentPlan?.priceId === plan.priceId;
                const buttonType = getButtonType(plan);
                
                // Get localized description from product metadata
                const getLocalizedDescription = () => {
                  const productName = plan.productName;
                  const product = billingData.availableProducts?.find(p => p.name === productName);
                  
                  if (product?.metadata) {
                    const currentLanguage = i18n.language || 'en';
                    const descriptionKey = `description_${currentLanguage}`;
                    
                    // Try to get the description for the current language
                    if (product.metadata[descriptionKey]) {
                      return product.metadata[descriptionKey];
                    }
                    
                    // Fallback to English if current language not available
                    if (product.metadata.description_en) {
                      return product.metadata.description_en;
                    }
                  }
                  
                  // Final fallback to the original description
                  return plan.description;
                };

                // Get localized features from product metadata
                const getLocalizedFeatures = () => {
                  const productName = plan.productName;
                  const product = billingData.availableProducts?.find(p => p.name === productName);
                  
                  if (product?.metadata) {
                    // Helper function to transform metadata into localized features
                    const transformLocalizedFeatures = (metadata: {[key: string]: string}): string[] => {
                      const features: string[] = [];
                      
                      // Define the desired order of features
                      const featureOrder = [
                        'brands',
                        'personas', 
                        'socials',
                        'links / QR Codes',
                        'ideas',
                        'posts',
                        'users'
                      ];
                      
                      // Common translations
                      const unlimited = t('billing.features.unlimited', 'Unlimited');
                      const upTo = t('billing.features.upTo', 'Up to');
                      
                      // Process features in the specified order
                      featureOrder.forEach(key => {
                        const value = metadata[key];
                        if (value) {
                          switch (key) {
                            case 'brands':
                              const brandsSingular = t('billing.features.brands', 'brand');
                              const brandsPlural = t('billing.features.brands_plural', 'brands');
                              features.push(`${value === '1000' ? unlimited : value} ${parseInt(value) > 1 ? brandsPlural : brandsSingular}`);
                              break;
                            case 'personas':
                              const personasSingular = t('billing.features.personas', 'persona');
                              const personasPlural = t('billing.features.personas_plural', 'personas');
                              features.push(`${value === '1000' ? unlimited : value} ${parseInt(value) > 1 ? personasPlural : personasSingular}`);
                              break;
                            case 'socials':
                              const socialsSingular = t('billing.features.socials', 'social platform');
                              const socialsPlural = t('billing.features.socials_plural', 'social platforms');
                              features.push(`${value} ${parseInt(value) > 1 ? socialsPlural : socialsSingular}`);
                              break;
                            case 'links / QR Codes':
                              const linksText = t('billing.features.links', 'links / QR codes');
                              features.push(`${value} ${linksText}`);
                              break;
                            case 'ideas':
                              const ideasText = t('billing.features.ideas', 'content ideas');
                              features.push(`${value === 'Unlimited' ? unlimited : value} ${ideasText}`);
                              break;
                            case 'posts':
                              const postsText = t('billing.features.posts', 'posts per month');
                              features.push(`${value === 'Unlimited' ? unlimited : value} ${postsText}`);
                              break;
                            case 'users':
                              const usersSingular = t('billing.features.users', 'team member');
                              const usersPlural = t('billing.features.users_plural', 'team members');
                              features.push(`${upTo} ${value} ${parseInt(value) > 1 ? usersPlural : usersSingular}`);
                              break;
                          }
                        }
                      });
                      
                      return features;
                    };
                    
                    return transformLocalizedFeatures(product.metadata);
                  }
                  
                  // Fallback to original features
                  return plan.features;
                };
                
                return (
                  <PricingCard
                    key={plan.priceId}
                    productName={plan.productName}
                    description={getLocalizedDescription()}
                    price={plan.formattedPrice}
                    monthlyPrice={plan.formattedMonthlyPrice}
                    buttonText={plan.buttonText}
                    features={getLocalizedFeatures()}
                    onSubscribe={() => checkoutMutation.mutate({ priceId: plan.priceId })}
                    isLoading={checkoutMutation.isPending}
                    billingPeriod={billingPeriod}
                    isCurrentPlan={isCurrentPlan}
                    buttonType={buttonType}
                  />
                );
              })}
            </div>

            {/* Billing History Card - Temporarily hidden */}
            {false && (
              <div className="bg-white rounded-xl border border-black shadow-lg overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">{t('billing.billingHistory')}</h2>
                </div>

                {!hasSubscription ? (
                  <div className="p-6 text-center text-gray-500">
                    {t('billing.noBillingHistory')}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('billing.invoiceNumber')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('billing.amount')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('billing.date')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('billing.status')}
                          </th>
                          <th scope="col" className="relative px-6 py-3">
                            <span className="sr-only">{t('billing.actions')}</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {billingData?.invoices?.map((invoice) => (
                          <tr key={invoice.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {invoice.number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatLocaleCurrency(invoice.amount_paid, invoice.currency, i18n.language)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatLocaleDate(invoice.created * 1000, i18n.language)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                                {invoice.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-3">
                                <a
                                  href={invoice.invoice_pdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-700"
                                >
                                  <Download className="w-4 h-4" />
                                </a>
                                <a
                                  href={invoice.hosted_invoice_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-700"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </PageLayout>
        )}
      </div>
    </div>
  );
};

export default Billing;